import { X } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useActiveEmployees, type ActiveEmployee , useSetSalary, type SetSalaryInput } from '@/hooks'
import { cn } from '@/lib/utils'

interface SalaryFormProps {
  onClose: () => void
  onSuccess?: () => void
}

export function SalaryForm({ onClose, onSuccess }: SalaryFormProps) {
  const setSalaryMutation = useSetSalary()
  const { data: employees } = useActiveEmployees()

  const [formData, setFormData] = useState({
    employeeId: '',
    baseSalary: '',
    allowance: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    remark: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.employeeId) {newErrors.employeeId = '请选择员工'}
    if (!formData.baseSalary) {newErrors.baseSalary = '请输入基本工资'}
    else if (isNaN(parseFloat(formData.baseSalary)) || parseFloat(formData.baseSalary) < 0) {
      newErrors.baseSalary = '请输入有效的金额'
    }
    if (formData.allowance && (isNaN(parseFloat(formData.allowance)) || parseFloat(formData.allowance) < 0)) {
      newErrors.allowance = '请输入有效的金额'
    }
    if (!formData.effectiveDate) {newErrors.effectiveDate = '请选择生效日期'}
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {return}

    const data: SetSalaryInput = {
      employeeId: formData.employeeId,
      baseSalary: Math.round(parseFloat(formData.baseSalary) * 100), // 转换为分
      allowance: formData.allowance ? Math.round(parseFloat(formData.allowance) * 100) : undefined,
      effectiveDate: formData.effectiveDate,
      remark: formData.remark || undefined,
    }
    setSalaryMutation.mutate(data, { onSuccess: () => { onSuccess?.(); onClose() } })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">设置薪资</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                员工 <span className="text-destructive">*</span>
              </label>
              <Select
                value={formData.employeeId || 'placeholder'}
                onValueChange={(value) => setFormData({ ...formData, employeeId: value === 'placeholder' ? '' : value })}
              >
                <SelectTrigger className={cn('w-full', errors.employeeId && 'border-destructive')}>
                  <SelectValue placeholder="请选择员工" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder">请选择员工</SelectItem>
                  {employees?.map((emp: ActiveEmployee) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.employeeId && <p className="mt-1 text-sm text-destructive">{errors.employeeId}</p>}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">
                基本工资 (元) <span className="text-destructive">*</span>
              </label>
              <input
                type="number"
                value={formData.baseSalary}
                onChange={(e) => setFormData({ ...formData, baseSalary: e.target.value })}
                placeholder="请输入基本工资"
                step="0.01"
                min="0"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.baseSalary && 'border-destructive'
                )}
              />
              {errors.baseSalary && <p className="mt-1 text-sm text-destructive">{errors.baseSalary}</p>}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">津贴 (元)</label>
              <input
                type="number"
                value={formData.allowance}
                onChange={(e) => setFormData({ ...formData, allowance: e.target.value })}
                placeholder="请输入津贴"
                step="0.01"
                min="0"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.allowance && 'border-destructive'
                )}
              />
              {errors.allowance && <p className="mt-1 text-sm text-destructive">{errors.allowance}</p>}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">
                生效日期 <span className="text-destructive">*</span>
              </label>
              <input
                type="date"
                value={formData.effectiveDate}
                onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.effectiveDate && 'border-destructive'
                )}
              />
              {errors.effectiveDate && <p className="mt-1 text-sm text-destructive">{errors.effectiveDate}</p>}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">备注</label>
              <textarea
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                placeholder="请输入备注"
                rows={2}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {setSalaryMutation.error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {setSalaryMutation.error.message}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted">
              取消
            </button>
            <button
              type="submit"
              disabled={setSalaryMutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {setSalaryMutation.isPending ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
